import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquareText, FileBadge2, Save, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const errMsg = (e) => e?.response?.data?.detail || e.message;

function initialValues(it) {
  const v = {};
  [...it.fields, ...it.buttons].forEach((f) => { v[f.key] = f.value; });
  if (it.kind === "template") { v.template_name = it.template_name; v.language = it.language; }
  return v;
}

function FieldRow({ itemKey, f, value, onChange, button }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold text-zinc-600">{f.label}{button && <span className="font-normal text-zinc-400"> · maks 20 karakter</span>}</span>
      {button ? (
        <Input value={value} maxLength={20} onChange={(e) => onChange(e.target.value)}
          className="h-9 rounded-sm text-[12px] max-w-xs" data-testid={`wa-msg-${itemKey}-${f.key}-input`} />
      ) : (
        <Textarea value={value} maxLength={1024} onChange={(e) => onChange(e.target.value)} rows={Math.min(6, Math.max(2, Math.ceil(value.length / 90)))}
          className="rounded-sm text-[12px] leading-relaxed" data-testid={`wa-msg-${itemKey}-${f.key}-textarea`} />
      )}
    </label>
  );
}

function TemplateFields({ it, v, set }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3">
      <label className="block space-y-1">
        <span className="text-[11px] font-semibold text-zinc-600">Nama template (template name)</span>
        <Input value={v.template_name} onChange={(e) => set("template_name", e.target.value)}
          className="h-9 rounded-sm text-[12px] font-mono" data-testid={`wa-msg-${it.key}-template-name-input`} />
      </label>
      <label className="block space-y-1">
        <span className="text-[11px] font-semibold text-zinc-600">Bahasa (language code)</span>
        <Input value={v.language} onChange={(e) => set("language", e.target.value)} placeholder="id"
          className="h-9 rounded-sm text-[12px] font-mono" data-testid={`wa-msg-${it.key}-language-input`} />
      </label>
    </div>
  );
}

function MessageCard({ it, onSaved }) {
  const [v, setV] = useState(() => initialValues(it));
  const [busy, setBusy] = useState("");
  useEffect(() => { setV(initialValues(it)); }, [it]);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const dirty = JSON.stringify(v) !== JSON.stringify(initialValues(it));
  const isTpl = it.kind === "template";

  async function save() {
    setBusy("save");
    try { await api.saveWaMessage(it.key, v); toast.success(`"${it.title}" disimpan`); onSaved(); }
    catch (e) { toast.error("Gagal simpan: " + errMsg(e)); }
    finally { setBusy(""); }
  }
  async function reset() {
    if (!window.confirm(`Kembalikan "${it.title}" ke teks bawaan?`)) return;
    setBusy("reset");
    try { await api.resetWaMessage(it.key); toast.success("Dikembalikan ke bawaan"); onSaved(); }
    catch (e) { toast.error("Gagal reset: " + errMsg(e)); }
    finally { setBusy(""); }
  }

  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-4 space-y-3" data-testid={`wa-msg-card-${it.key}`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 p-1.5 rounded-sm text-white ${isTpl ? "bg-sky-600" : "bg-emerald-600"}`}>
          {isTpl ? <FileBadge2 className="w-4 h-4" /> : <MessageSquareText className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[13px] font-semibold text-zinc-900">{it.title}</h3>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider font-semibold text-white ${isTpl ? "bg-sky-600" : "bg-emerald-600"}`}>
              {isTpl ? "Template Meta" : "Teks bebas"}
            </span>
          </div>
          <p className="text-[11px] text-zinc-500 mt-0.5" data-testid={`wa-msg-${it.key}-trigger`}>{it.trigger}</p>
        </div>
      </div>
      {isTpl ? <TemplateFields it={it} v={v} set={set} /> : (
        <div className="space-y-3">
          {it.fields.map((f) => <FieldRow key={f.key} itemKey={it.key} f={f} value={v[f.key]} onChange={(x) => set(f.key, x)} />)}
          {it.buttons.map((f) => <FieldRow key={f.key} itemKey={it.key} f={f} value={v[f.key]} onChange={(x) => set(f.key, x)} button />)}
        </div>
      )}
      <div className="flex items-center gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={reset} disabled={!!busy} className="h-8 rounded-sm gap-1 text-[12px] text-zinc-500"
          data-testid={`wa-msg-${it.key}-reset-btn`}><RotateCcw className="w-3.5 h-3.5" /> Bawaan</Button>
        <Button size="sm" onClick={save} disabled={!!busy || !dirty} className="h-8 rounded-sm gap-1 text-[12px]"
          data-testid={`wa-msg-${it.key}-save-btn`}>
          {busy === "save" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Simpan
        </Button>
      </div>
    </div>
  );
}

export default function WaMessages() {
  const [items, setItems] = useState(null);
  const load = () => api.listWaMessages().then((d) => setItems(d.items)).catch((e) => toast.error("Gagal memuat: " + errMsg(e)));
  useEffect(() => { load(); }, []);
  const groups = useMemo(() => {
    const g = {};
    (items || []).forEach((it) => { (g[it.group] ||= []).push(it); });
    return Object.entries(g);
  }, [items]);

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="wa-messages-page">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">Kelola Pesan Otomatis</h1>
        <p className="text-[12px] text-zinc-500 mt-1 max-w-3xl">
          Semua pesan WhatsApp yang dikirim otomatis oleh sistem. Data seperti nama, tanggal, jam, alamat, dan link
          tetap diisi otomatis; yang bisa diedit adalah kalimat tetapnya. Isi template Meta dikelola di Meta Business Manager.
        </p>
      </div>
      {!items && <div className="text-[12px] text-zinc-500">Memuat…</div>}
      {groups.map(([name, list]) => (
        <section key={name} className="space-y-3" data-testid={`wa-msg-group-${name}`}>
          <h2 className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500">{name}</h2>
          {list.map((it) => <MessageCard key={it.key} it={it} onSaved={load} />)}
        </section>
      ))}
    </div>
  );
}
